/**
 * Header utilities for API requests
 *
 * Idempotency key generation, header building, and header sanitization.
 */

import {
  IDEMPOTENT_METHODS,
  SENSITIVE_HEADER_PREFIXES,
} from '@/src/config/api-constants';

/**
 * Generates a random idempotency key using browser crypto APIs.
 * Falls back to timestamp-based key if crypto APIs unavailable.
 *
 * @returns UUID v4-formatted idempotency key
 *
 * @example
 * ```typescript
 * const key = generateIdempotencyKey();
 * // Returns: "550e8400-e29b-41d4-a716-446655440000"
 * ```
 */
function generateIdempotencyKey(): string {
  const globalCrypto =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (globalCrypto?.randomUUID) {
    return globalCrypto.randomUUID();
  }
  if (globalCrypto?.getRandomValues) {
    const buffer = globalCrypto.getRandomValues(new Uint8Array(16));
    const hex = Array.from(buffer, byte =>
      byte.toString(16).padStart(2, '0')
    ).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `idem_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

/**
 * Creates a new idempotency key for API requests.
 * Public wrapper around generateIdempotencyKey.
 *
 * @returns UUID v4-formatted idempotency key
 *
 * @example
 * ```typescript
 * const key = createIdempotencyKey();
 * await apiClient.post('/widgets', data, schema, context, { idempotencyKey: key });
 * ```
 */
export function createIdempotencyKey(): string {
  return generateIdempotencyKey();
}

/**
 * Builds request headers with Content-Type and optional idempotency key.
 * Automatically adds idempotency key for POST/PUT/PATCH/DELETE if autoIdempotency enabled.
 *
 * @param existing - Existing headers to merge
 * @param method - HTTP method (uppercase)
 * @param autoIdempotency - Whether to auto-generate idempotency keys
 * @returns Headers object with merged headers
 *
 * @example
 * ```typescript
 * const headers = buildHeaders({ 'X-Custom': 'value' }, 'POST', true);
 * // Headers include: Content-Type, X-Custom, Idempotency-Key
 * ```
 */
export function buildHeaders(
  existing: HeadersInit | undefined,
  method: string,
  autoIdempotency: boolean
): Headers {
  const headers = new Headers(existing ?? {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (
    autoIdempotency &&
    (IDEMPOTENT_METHODS as readonly string[]).includes(method) &&
    !headers.has('Idempotency-Key')
  ) {
    headers.set('Idempotency-Key', generateIdempotencyKey());
  }
  return headers;
}

/**
 * Sanitizes headers by removing sensitive values before logging.
 * Filters out Authorization, Cookie, and other sensitive headers.
 *
 * **Security:** Prevents credential leakage in logs and monitoring.
 *
 * @param headers - Headers object to sanitize
 * @returns Record of non-sensitive headers, or undefined if all filtered
 *
 * @example
 * ```typescript
 * const headers = new Headers({
 *   'Content-Type': 'application/json',
 *   'Authorization': 'Bearer secret-token'
 * });
 * const safe = sanitizeHeaders(headers);
 * // Returns: { 'Content-Type': 'application/json' }
 * // Authorization header filtered out
 * ```
 */
export function sanitizeHeaders(
  headers: Headers
): Record<string, string> | undefined {
  const entries: Array<[string, string]> = [];
  for (const [key, value] of headers.entries()) {
    const lower = key.toLowerCase();
    if (SENSITIVE_HEADER_PREFIXES.some(prefix => lower.startsWith(prefix))) {
      continue;
    }
    entries.push([key, value]);
  }
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries);
}
